import { logError } from '@/utils/logger';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import {
  SquaresFour, Users, UserCircleGear, CalendarCheck,
  CalendarBlank, CalendarX, FileText, Package, CurrencyDollar,
  GearSix, ChatCircleDots, Trash, UserGear, MapTrifold, FolderOpen
} from '@phosphor-icons/react';

const Sidebar = ({ mobileOpen, setMobileOpen, collapsed }) => {
  const { t } = useTranslation();
  const [userPermissions, setUserPermissions] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          // Utilise les permissions EFFECTIVES (custom priment sur rôle)
          setUserPermissions(data.effective_permissions || {});
        }
      } catch (error) { logError('Failed to fetch user:', error); }
    };
    fetchUser();
  }, []);

  const allNavItems = [
    { path: '/dashboard', icon: SquaresFour, label: t('nav.dashboard'), module: 'dashboard' },
    { path: '/clients', icon: Users, label: t('nav.clients'), module: 'clients' },
    { path: '/agents', icon: UserCircleGear, label: t('nav.agents'), module: 'agents' },
    { path: '/assignments', icon: CalendarCheck, label: t('nav.assignments'), module: 'assignments' },
    { path: '/supervisors', icon: UserGear, label: 'Superviseurs', module: 'supervisors' },
    { path: '/sites-map', icon: MapTrifold, label: 'Carte des Sites', module: 'sites_map' },
    { path: '/projects', icon: FolderOpen, label: 'Projets', module: 'projects' },
    { path: '/calendar', icon: CalendarBlank, label: 'Calendrier', module: 'calendar' },
    { path: '/alerts-calendar', icon: CalendarX, label: 'Calendrier Alertes', module: 'alerts_calendar' },
    { path: '/reports', icon: FileText, label: t('nav.reports'), module: 'reports' },
    { path: '/logistics', icon: Package, label: t('nav.logistics'), module: 'logistics' },
    { path: '/finance', icon: CurrencyDollar, label: 'Finance', module: 'finance' },
    { path: '/messages', icon: ChatCircleDots, label: 'Messages', module: 'messages' },
    { path: '/trash', icon: Trash, label: 'Corbeille', module: 'trash' },
    { path: '/admin', icon: GearSix, label: t('nav.admin'), module: 'admin' },
  ];

  const navItems = allNavItems.filter(item => {
    if (!userPermissions) return item.module === 'dashboard';
    return userPermissions[item.module]?.read === true;
  });

  return (
    <aside
      data-testid="sidebar"
      className={`
        fixed inset-y-0 left-0 z-50
        bg-[#0F172A] border-r border-[#1E293B]
        transition-all duration-300 ease-in-out flex flex-col
        ${mobileOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0'}
        ${collapsed ? 'lg:w-[68px]' : 'lg:w-64'}
      `}
    >
      {/* Logo */}
      <div className="h-16 border-b border-[#1E293B] flex items-center px-4 overflow-hidden">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src="https://customer-assets.emergentagent.com/job_security-ops-hub-10/artifacts/gm0w9z4f_FLAVIGON.png"
            alt="Gazelle Security"
            className="h-9 w-9 object-contain flex-shrink-0"
          />
          <h1 className={`text-lg font-bold text-[#F8FAFC] tracking-tight whitespace-nowrap transition-opacity duration-200 ${collapsed ? 'lg:opacity-0 lg:w-0 lg:overflow-hidden' : 'opacity-100'}`}>
            Gazelle Security
          </h1>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto overflow-x-hidden">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.path.slice(1)}`}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md transition-colors group ${
                collapsed ? 'lg:px-0 lg:justify-center px-3 py-3' : 'px-3 py-3'
              } ${
                isActive
                  ? 'bg-[#2563EB] text-white'
                  : 'text-[#94A3B8] hover:bg-[#1E293B] hover:text-white'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon size={22} weight={isActive ? 'fill' : 'regular'} className="flex-shrink-0" />
                <span className={`font-medium whitespace-nowrap transition-opacity duration-200 ${collapsed ? 'lg:hidden' : ''}`}>
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
